const DEFAULT_PASTURE_PRESETS = Object.freeze({
  "Braquiária": { uaPerHa: 2.0, entryHeight: 30, exitHeight: 15 },
  "Andropogon": { uaPerHa: 1.6, entryHeight: 35, exitHeight: 20 },
  "Mombaça": { uaPerHa: 3.0, entryHeight: 90, exitHeight: 45 },
  "Tanzânia": { uaPerHa: 2.8, entryHeight: 75, exitHeight: 35 },
  "Tifton": { uaPerHa: 3.5, entryHeight: 25, exitHeight: 12 },
  "Coastcross": { uaPerHa: 3.0, entryHeight: 20, exitHeight: 10 },
  "Estrela": { uaPerHa: 2.6, entryHeight: 25, exitHeight: 12 },
  "Capim Nativo": { uaPerHa: 1.0, entryHeight: 35, exitHeight: 20 },
  "Outro": { uaPerHa: 1.8, entryHeight: 30, exitHeight: 15 },
  "Grama": { uaPerHa: 0, entryHeight: 0, exitHeight: 0 },
});

const DEFAULT_WEED_FACTORS = Object.freeze({
  "Limpo": 1,
  "Pouco": 0.92,
  "Médio": 0.8,
  "Sujo": 0.62,
  "Muito Sujo": 0.42,
});

const DEFAULT_HEIGHT_FACTORS = Object.freeze({
  too_low: 0.65,
  ideal: 1,
  moderately_high: 0.93,
  too_high: 0.82,
});

function toPositiveNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

function normalizePastureCapacityConfig(input = {}) {
  const base = input && typeof input === 'object' ? input : {};
  const pastureTypesRaw = base.pastureTypes && typeof base.pastureTypes === 'object' ? base.pastureTypes : {};
  const weedFactorsRaw = base.weedFactors && typeof base.weedFactors === 'object' ? base.weedFactors : {};
  const heightFactorsRaw = base.heightFactors && typeof base.heightFactors === 'object' ? base.heightFactors : {};

  const pastureTypes = {};
  const seedKeys = new Set([...Object.keys(DEFAULT_PASTURE_PRESETS), ...Object.keys(pastureTypesRaw)]);
  for (const key of seedKeys) {
    const fallback = DEFAULT_PASTURE_PRESETS[key] || DEFAULT_PASTURE_PRESETS.Outro;
    const raw = pastureTypesRaw[key] && typeof pastureTypesRaw[key] === 'object' ? pastureTypesRaw[key] : {};
    pastureTypes[key] = {
      uaPerHa: toPositiveNumber(raw.uaPerHa, fallback.uaPerHa),
      entryHeight: toPositiveNumber(raw.entryHeight, fallback.entryHeight),
      exitHeight: toPositiveNumber(raw.exitHeight, fallback.exitHeight),
    };
  }

  const weedFactors = {};
  for (const key of new Set([...Object.keys(DEFAULT_WEED_FACTORS), ...Object.keys(weedFactorsRaw)])) {
    weedFactors[key] = toPositiveNumber(weedFactorsRaw[key], DEFAULT_WEED_FACTORS[key] ?? 1);
  }

  const heightFactors = {
    too_low: toPositiveNumber(heightFactorsRaw.too_low, DEFAULT_HEIGHT_FACTORS.too_low),
    ideal: toPositiveNumber(heightFactorsRaw.ideal, DEFAULT_HEIGHT_FACTORS.ideal),
    moderately_high: toPositiveNumber(heightFactorsRaw.moderately_high, DEFAULT_HEIGHT_FACTORS.moderately_high),
    too_high: toPositiveNumber(heightFactorsRaw.too_high, DEFAULT_HEIGHT_FACTORS.too_high),
  };

  return {
    pastureTypes,
    weedFactors,
    heightFactors,
  };
}

function resolvePasturePreset(config, grassType) {
  const normalized = normalizePastureCapacityConfig(config);
  return normalized.pastureTypes[grassType] || normalized.pastureTypes.Outro || DEFAULT_PASTURE_PRESETS.Outro;
}

function classifyHeightFactor(currentHeight, preset, config) {
  const normalized = normalizePastureCapacityConfig(config);
  const height = Number(currentHeight);
  if (!Number.isFinite(height) || height <= 0) return normalized.heightFactors.ideal;
  const entry = toPositiveNumber(preset?.entryHeight, 0);
  const exit = toPositiveNumber(preset?.exitHeight, 0);
  if (!entry || !exit || entry <= exit) return normalized.heightFactors.ideal;
  if (height < exit) return normalized.heightFactors.too_low;
  if (height <= entry) return normalized.heightFactors.ideal;
  if (height <= entry * 1.3) return normalized.heightFactors.moderately_high;
  return normalized.heightFactors.too_high;
}

function computePastureCapacity(record, config) {
  if (!record || record.useAnimalCapacity === false) {
    return {
      uaPerHa: '', uaTotal: '', animalEquivalent: '', baseUaPerHa: '',
      weedFactor: '', heightFactor: '', entryHeight: '', exitHeight: '',
    };
  }
  const normalized = normalizePastureCapacityConfig(config);
  const preset = resolvePasturePreset(normalized, record.grassType);
  const area = toPositiveNumber(record.areHa, 0);
  const weedFactor = normalized.weedFactors[String(record.weedLevel || '').trim()] ?? 1;
  const heightFactor = classifyHeightFactor(record.currentHeight, preset, normalized);
  const baseUaPerHa = toPositiveNumber(preset.uaPerHa, 0);
  const uaPerHaValue = Number((baseUaPerHa * weedFactor * heightFactor).toFixed(2));
  const uaTotalValue = Number((uaPerHaValue * area).toFixed(2));
  const animalEquivalent = Number((uaTotalValue * 450 / 450).toFixed(2));
  return {
    uaPerHa: uaPerHaValue ? uaPerHaValue.toFixed(2) : '0.00',
    uaTotal: uaTotalValue ? uaTotalValue.toFixed(2) : '0.00',
    animalEquivalent: animalEquivalent ? animalEquivalent.toFixed(2) : '0.00',
    baseUaPerHa: baseUaPerHa ? baseUaPerHa.toFixed(2) : '0.00',
    weedFactor: Number(weedFactor || 1).toFixed(2),
    heightFactor: Number(heightFactor || 1).toFixed(2),
    entryHeight: String(toPositiveNumber(record.entryHeight, preset.entryHeight || 0) || ''),
    exitHeight: String(toPositiveNumber(record.exitHeight, preset.exitHeight || 0) || ''),
  };
}

export {
  DEFAULT_PASTURE_PRESETS,
  DEFAULT_WEED_FACTORS,
  DEFAULT_HEIGHT_FACTORS,
  normalizePastureCapacityConfig,
  resolvePasturePreset,
  computePastureCapacity,
};
